import { PoMultiselectOption } from '@po-ui/ng-components';

/**
 * Rótulo de tela das categorias de sugestão, no vocabulário em que o backend as grava
 * (`job.CanonicalCategory`: minúsculo, sem acento, `documentacao` → `docs`). Categoria
 * desconhecida aparece como veio — o backend devolve toda categoria presente, e esconder
 * uma nova faria a soma parar de bater com as sugestões.
 */
const LABELS: Record<string, string> = {
  desempenho: 'Desempenho', legibilidade: 'Legibilidade', estilo: 'Estilo',
  testes: 'Testes', arquitetura: 'Arquitetura', seguranca: 'Segurança',
  nomenclatura: 'Nomenclatura', docs: 'Documentação',
  confiabilidade: 'Confiabilidade', dados: 'Dados',
  manutenibilidade: 'Manutenibilidade', nao_informada: 'Não informada'
};

/**
 * Opções do filtro dedicado de categoria (ranking de fontes). `nao_informada` fica de
 * fora: não é uma categoria gravada, é o rótulo de quando ela falta — filtrar por ela
 * nunca bate com nada.
 */
export const CATEGORY_OPTIONS: PoMultiselectOption[] = Object.keys(LABELS)
  .filter(key => key !== 'nao_informada')
  .map(key => ({ value: key, label: LABELS[key] }));

export function reviewCategoryLabel(category: string): string {
  return LABELS[category] ?? category;
}

/**
 * Categorias com sugestão, da maior para a menor: `"Legibilidade (3), Testes (1)"`.
 * `limit` corta as primeiras; sem ele, vêm todas.
 */
export function describeCategories(categories: Record<string, number> | undefined, limit?: number): string {
  const entries = Object.entries(categories ?? {})
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  return (limit ? entries.slice(0, limit) : entries)
    .map(([name, count]) => `${reviewCategoryLabel(name)} (${count})`)
    .join(', ');
}

/** Uma categoria com sugestão, pronta para virar tag: `"Testes (5)"`. */
export interface CategoryTag {
  label: string;
  count: number;
}

/**
 * As categorias em tags: as principais e, se houver mais, quantas são e quais — para uma
 * tag "+N" com as demais no tooltip.
 *
 * Com `pinned` (o filtro dedicado de categoria ativo), mostra TODAS — a lista já é mais
 * curta quando filtrada, e completude importa mais que compactação: sem isso, filtrar
 * por uma categoria pouco frequente no fonte podia deixá-la fora do corte de `limit`, e
 * a linha parecia não ter nada a ver com o que foi pedido. Sem filtro, corta em `limit`
 * para a célula não quebrar em duas linhas — `moreTitle` leva o resto pro tooltip.
 */
export function categoryTags(
  categories: Record<string, number> | undefined, limit: number, pinned: string[] = []
): { tags: CategoryTag[]; more: number; moreTitle: string } {
  const entries = Object.entries(categories ?? {})
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([name, count]) => ({ label: `${reviewCategoryLabel(name)} (${count})`, count }));

  if (pinned.length > 0 || entries.length <= limit) {
    return { tags: entries, more: 0, moreTitle: '' };
  }

  const shown = Math.max(limit - 1, 1);
  const rest = entries.slice(shown);
  return {
    tags: entries.slice(0, shown),
    more: rest.length,
    moreTitle: rest.map(entry => entry.label).join(', ')
  };
}
